// src/pages/StudentsPage.js
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { students as studentsApi, settings } from '../utils/api';
import ConfirmModal from '../components/ConfirmModal';

const STATUS_BADGE = { active: 'badge-success', suspended: 'badge-danger', graduated: 'badge-info', withdrawn: 'badge-muted' };
const GENDER_LABEL = { male: '♂ Male', female: '♀ Female' };

export default function StudentsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const deleteMut = useMutation({
    mutationFn: (id) => studentsApi.delete(id),
    onSuccess: () => { toast.success('Student deleted'); qc.invalidateQueries(['students']); setConfirmDelete(null); },
    onError: err => { toast.error(err.response?.data?.message || 'Failed to delete'); setConfirmDelete(null); }
  });
  const [filters, setFilters] = useState({
    search: '', course_id: '', institution_id: '', academic_year_id: '',
    gender: '', status: '', country_code: ''
  });
  const [applied, setApplied] = useState(filters);

  const { data, isLoading } = useQuery({
    queryKey: ['students', page, applied],
    queryFn: () => studentsApi.list({ ...applied, page, limit: 10 }).then(r => r.data)
  });

  const { data: courses } = useQuery({ queryKey: ['courses'], queryFn: () => settings.courses().then(r => r.data.data) });
  const { data: institutions } = useQuery({ queryKey: ['institutions'], queryFn: () => settings.institutions().then(r => r.data.data) });
  const { data: years } = useQuery({ queryKey: ['years'], queryFn: () => settings.academicYears().then(r => r.data.data) });

  const applyFilters = () => { setApplied({ ...filters }); setPage(1); };
  const clearFilters = () => {
    const empty = { search: '', course_id: '', institution_id: '', academic_year_id: '', gender: '', status: '', country_code: '' };
    setFilters(empty); setApplied(empty); setPage(1);
  };

  const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Students</h1>
        <Link to="/students/new" className="btn btn-primary">+ Add Student</Link>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="form-grid-3" style={{ marginBottom: 12 }}>
          <div className="search-input">
            <span className="search-icon">🔍</span>
            <input className="form-control" placeholder="Search name, reg no, email…"
              value={filters.search} onChange={e => set('search', e.target.value)}
              onKeyDown={e => e.key === 'Enter' && applyFilters()} />
          </div>
          <select className="form-control" value={filters.course_id} onChange={e => set('course_id', e.target.value)}>
            <option value="">All Courses</option>
            {courses?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select className="form-control" value={filters.institution_id} onChange={e => set('institution_id', e.target.value)}>
            <option value="">All Schools</option>
            {institutions?.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
          </select>
          <select className="form-control" value={filters.academic_year_id} onChange={e => set('academic_year_id', e.target.value)}>
            <option value="">All Years</option>
            {years?.map(y => <option key={y.id} value={y.id}>{y.label}</option>)}
          </select>
          <select className="form-control" value={filters.gender} onChange={e => set('gender', e.target.value)}>
            <option value="">All Genders</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
          <select className="form-control" value={filters.status} onChange={e => set('status', e.target.value)}>
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="graduated">Graduated</option>
            <option value="withdrawn">Withdrawn</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-primary btn-sm" onClick={applyFilters}>Apply Filters</button>
          <button className="btn btn-secondary btn-sm" onClick={clearFilters}>Clear</button>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {isLoading ? (
          <div style={{ padding: '40px', textAlign: 'center' }}><span className="spinner" /></div>
        ) : (
          <>
            <div style={{ marginBottom: 12, color: 'var(--text-muted)', fontSize: '0.82rem' }}>
              Showing {data?.data?.length || 0} of {data?.pagination?.total || 0} students
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Reg. No</th><th>Name</th><th>Gender</th><th>Course</th>
                    <th>School</th><th>Year</th><th>Nationality</th>
                    <th>Devices</th><th>Status</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.data?.length === 0 && (
                    <tr><td colSpan={10}><div className="empty-state"><div className="icon">👤</div>No students found</div></td></tr>
                  )}
                  {data?.data?.map(s => (
                    <tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/students/${s.id}`)}>
                      <td><span className="text-mono">{s.registration_no}</span></td>
                      <td><strong>{s.full_name}</strong></td>
                      <td style={{ fontSize: '0.8rem' }}>{GENDER_LABEL[s.gender] || s.gender}</td>
                      <td className="cell-wrap">{s.course_name || '—'}</td>
                      <td className="cell-wrap">{s.institution_name || '—'}</td>
                      <td>{s.academic_year_label || '—'}</td>
                      <td>{s.nationality || '—'}</td>
                      <td>
                        <span className={`badge ${s.device_count > 0 ? 'badge-primary' : 'badge-muted'}`}>
                          {s.device_count}
                        </span>
                      </td>
                      <td><span className={`badge ${STATUS_BADGE[s.status]}`}>{s.status}</span></td>
                      <td onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <Link to={`/students/${s.id}`} className="btn btn-secondary btn-sm">View</Link>
                          <Link to={`/students/${s.id}/edit`} className="btn btn-secondary btn-sm">Edit</Link>
                          <button className="btn btn-danger btn-sm" onClick={() => setConfirmDelete(s)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data?.pagination?.pages > 1 && (
              <div className="pagination">
                <button onClick={() => setPage(p => p - 1)} disabled={page === 1}>‹ Prev</button>
                {Array.from({ length: Math.min(7, data.pagination.pages) }, (_, i) => {
                  const p = i + 1;
                  return <button key={p} className={page === p ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>;
                })}
                <button onClick={() => setPage(p => p + 1)} disabled={page >= data.pagination.pages}>Next ›</button>
              </div>
            )}
          </>
        )}
      </div>
      {confirmDelete && (
        <ConfirmModal
          title="Delete Student"
          message={`Are you sure you want to delete ${confirmDelete.full_name}? This cannot be undone.`}
          loading={deleteMut.isPending}
          onConfirm={() => deleteMut.mutate(confirmDelete.id)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}
